import { useUpdateAccount } from '@my/directory-api/hooks';
import { useSupportSession } from '@my/platform/support';
import { Button, ErrorBanner, FormSection, TextInput, showToast } from '@my/ui';
import { useEffect, useState } from 'react';
import { OpenInAppPanel } from './OpenInAppPanel';
import { useAccountContext } from './useAccountContext';

/**
 * What this account is, and the way through to the apps.
 *
 * <p>
 * Written as the customer's own page, like every section here. The one
 * staff-shaped thing is the domain field — see below — and it is disabled
 * rather than hidden, because an owner who cannot change their domain should
 * still be able to SEE it.
 * </p>
 */
export function AccountOverviewPage() {
  const { account, isReady } = useAccountContext();
  const { isSupportSession } = useSupportSession();
  const update = useUpdateAccount();

  const [name, setName] = useState(account.name);
  const [domain, setDomain] = useState(account.domain);

  // Re-seed when the strip switches customer: this component stays mounted
  // across that change, so without it the previous account's values would sit
  // in the form ready to be saved onto the new one.
  useEffect(() => {
    setName(account.name);
    setDomain(account.domain);
  }, [account.id, account.name, account.domain]);

  const isDirty = name.trim() !== account.name || domain.trim() !== account.domain;
  const canSave = isReady && isDirty && name.trim().length > 0 && domain.trim().length > 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;

    update.mutate(
      { name: name.trim(), domain: domain.trim() },
      {
        onSuccess: () => showToast({ type: 'success', message: 'Account updated.' }),
      },
    );
  };

  return (
    <>
      {update.error && (
        <ErrorBanner title="Could not save the account" message={update.error.message} />
      )}

      <form onSubmit={submit}>
        <FormSection
          title="Account Details"
         
          columns={3}
          footer={
            <Button type="submit" disabled={!canSave || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          }
        >
          <TextInput
            label="Account name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          {/* Centred: these two are single digits sitting in a column as wide
              as the inputs beside them, so left-aligned they read as stranded
              at one edge of a lot of empty space. */}
          <ReadOnly label="Organisations" value={String(account.organisations.length)} center />
          <ReadOnly label="Created" value={formatDate(account.createdUtc)} />

          {/*
            Second row, under the account name: the two editable fields then sit
            in one column and the read-only facts in the others, instead of the
            domain's helper text pushing a row of plain values out of line.

            SUPPORT ONLY. The domain is how an email address resolves to an
            account, and changing it rewrites nothing already issued — users
            keep their own login addresses either way. It is a step in a real
            domain move, not a setting, so the server refuses a change from
            anybody else and this input matches that rather than letting
            somebody type into a field that will reject them.
          */}
          <TextInput
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={!isSupportSession}
            helperText={
              isSupportSession
                ? 'Changing this affects how new users are matched to this account.'
                : 'Contact Stratum to change this.'
            }
            required
          />

          <ReadOnly
            label="Enabled apps"
            value={String(account.subscriptions.filter((s) => s.isActive).length)}
            center
          />

          {/*
            SUPPORT ONLY, and hidden rather than disabled — unlike the domain
            above, which an owner should see even though they cannot change it.
            The account id is an internal key: it answers nothing a customer
            would ask, and putting it on their screen invites it into support
            emails and screenshots as if it were something they should quote.
          */}
          {isSupportSession && <ReadOnly label="Account id" value={account.id} mono />}
        </FormSection>
      </form>

      <FormSection title="Open in app" columns={1}>
        <OpenInAppPanel account={account} />
      </FormSection>
    </>
  );
}

/**
 * Renders like a `TextInput` label/value pair without being a form control —
 * a disabled input would suggest these become editable under some condition,
 * and none of them ever do.
 */
function ReadOnly({
  label,
  value,
  mono,
  center,
}: {
  label: string;
  value: string;
  mono?: boolean;
  center?: boolean;
}) {
  return (
    <div className={`text-sm ${center ? 'text-center' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? 'font-mono text-xs text-slate-800' : 'text-sm text-slate-800'}>
        {value}
      </div>
    </div>
  );
}

/** Date only: the hour an account was created has never answered anything. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
